import Checkbox from "../../../components/checkbox/checkbox";
import PillTitle from "../../../components/pillTitle/pillTitle";
import { useI18n } from "../../../utils/translate/i18n/useI18n";
import { hasMicrophoneSupport } from "../../../type/verbalTest";
import type { VerbalTestParametersProps } from "../../../type/verbalTest";
import { WRS_WORDS_PER_LEVEL_EXT } from "../../../utils/config";
import "./verbalTestParameters.scss";

type VerbalTestParametersContentProps = Omit<VerbalTestParametersProps, "step"> & {
  locked?: boolean;
};

export function VerbalTestParametersContent({
  useMicrophone,
  onToggleMicrophone,
  doWRS,
  onToggleDoWRS,
  doSRT,
  onToggleDoSRT,
  useSentences,
  onToggleUseSentences,
  extendedWRS,
  onToggleExtendedWRS,
  locked = false,
}: VerbalTestParametersContentProps) {
  const { t } = useI18n();
  const micAvailable = hasMicrophoneSupport();

  return (
    <div className="panel--parameters--container">
      <div className="panel--parameters--checkboxplace">
        <Checkbox
          label={t("Mikrofon")}
          description={
            micAvailable
              ? t("Powtarzaj słowa głośno — test użyje mikrofonu do oceny odpowiedzi.")
              : t("Mikrofon / rozpoznawanie mowy nie jest obsługiwane przez tę przeglądarkę.")
          }
          checked={useMicrophone && micAvailable}
          onChange={onToggleMicrophone}
          disabled={locked || !micAvailable}
          variant="compact"
          icon="bi-mic-fill"
          infoTitle={t("Tryb mikrofonu — informacja")}
          infoText={t("Kiedy mikrofon jest włączony, aplikacja nagrywa Twój głos i porównuje go z testowanym słowem. Działa w przeglądarkach Chrome i Edge. Gdy mikrofon jest wyłączony, wyświetlane są 4 opcje do wyboru — wskazujesz słowo, które usłyszałeś/aś.")}
        />
      </div>
      <div className="panel--parameters--checkboxplace">
        <Checkbox
          label={t("Zrób test WRS")}
          description={t("Gdy włączone, zostanie wykonany test rozpoznawania słów (WRS).")}
          checked={doWRS}
          onChange={onToggleDoWRS}
          disabled={locked || !doSRT}
          variant="compact"
          icon="bi-list-check"
          infoTitle={t("Test WRS — informacja")}
          infoText={t("WRS (Word Recognition Score) — test rozpoznawania słów jednosylabowych. Badany słyszy słowo i musi je powtórzyć lub wybrać spośród opcji. Wynik wyrażony jest w procentach poprawnie rozpoznanych słów. Pozwala ocenić zdolność do rozumienia mowy przy określonym poziomie głośności.")}
        />
      </div>
      <div className="panel--parameters--checkboxplace">
        <Checkbox
          label={t("Zrób test SRT")}
          description={t("Gdy włączone, zostanie wykonany test progu rozumienia mowy (SRT).")}
          checked={doSRT}
          onChange={onToggleDoSRT}
          disabled={locked || !doWRS}
          variant="compact"
          icon="bi-bar-chart-steps"
          infoTitle={t("Test SRT — informacja")}
          infoText={t("SRT (Speech Reception Threshold) — próg rozumienia mowy. Badany słyszy dwuzgłoskowe słowa przy różnych poziomach głośności. Wynik to najniższy poziom dB, przy którym rozumie 50% słów. Pozwala określić minimalny poziom natężenia mowy konieczny do jej zrozumienia.")}
        />
      </div>
      <div className="panel--parameters--checkboxplace">
        <Checkbox
          label={t("Tryb zdaniowy")}
          description={t("Gdy włączone, test będzie używał całych zdań zamiast pojedynczych słów.")}
          checked={useSentences}
          onChange={onToggleUseSentences}
          disabled={locked}
          variant="compact"
          icon="bi-chat-text"
          infoTitle={t("Tryb zdaniowy — informacja")}
          infoText={t("W trybie zdaniowym (wg wzorca BKB-PL / HINT-PL) zamiast pojedynczych słów odtwarzane są krótkie zdania codzienne. Metoda ta jest stosowana w klinicznej audiometrii mowy jako uzupełnienie testów słownych — pozwala ocenić rozumienie mowy w warunkach zbliżonych do naturalnych. Zdania są fonetycznie zróżnicowane i opracowane zgodnie z wytycznymi audiologicznymi. Wynik testu interpretuje się analogicznie jak przy słowach.")}
        />
      </div>
      <div className="panel--parameters--checkboxplace">
        <Checkbox
          label={t("Badanie rozszerzone")}
          description={t("Gdy włączone, prezentowanych jest {{count}} słów na poziom dB zamiast standardowych 6.").replace("{{count}}", String(WRS_WORDS_PER_LEVEL_EXT))}
          checked={extendedWRS}
          onChange={onToggleExtendedWRS}
          disabled={locked || !doWRS}
          variant="compact"
          icon="bi-list-ul"
          infoTitle={t("Badanie rozszerzone — informacja")}
          infoText={t("Badanie rozszerzone (WRS Extended) zwiększa liczbę słów prezentowanych na każdym poziomie głośności z 6 do 10. Próg wczesnego zatrzymania podnoszony jest odpowiednio z 3 do 6 słów. Badanie trwa dłużej, ale daje dokładniejszy wynik — szczególnie przydatne w przypadkach granicznych lub przy ocenie klinicznej.")}
        />
      </div>
    </div>
  );
}

export default function VerbalTestParameters({
  step,
  useMicrophone,
  onToggleMicrophone,
  doWRS,
  onToggleDoWRS,
  doSRT,
  onToggleDoSRT,
  useSentences,
  onToggleUseSentences,
  extendedWRS,
  onToggleExtendedWRS,
  gender,
  ageCategory,
  onChangeGender,
  onChangeAgeCategory,
}: VerbalTestParametersProps) {
  const { t } = useI18n();
  const isLocked = step !== "params";

  return (
    <div className={`panel panel--light panel--parameters card border-0 mt-3${isLocked ? " panel--parameters--locked" : ""}`}>
      <div className="card-body text-start panel--parameters__main">
        <PillTitle as="div" variant="blue-brighter" iconClassName="bi bi-sliders2" text={t("Parametry badania")} />
        <VerbalTestParametersContent
          useMicrophone={useMicrophone}
          onToggleMicrophone={onToggleMicrophone}
          doWRS={doWRS}
          onToggleDoWRS={onToggleDoWRS}
          doSRT={doSRT}
          onToggleDoSRT={onToggleDoSRT}
          useSentences={useSentences}
          onToggleUseSentences={onToggleUseSentences}
          extendedWRS={extendedWRS}
          onToggleExtendedWRS={onToggleExtendedWRS}
          gender={gender}
          ageCategory={ageCategory}
          onChangeGender={onChangeGender}
          onChangeAgeCategory={onChangeAgeCategory}
          locked={isLocked}
        />
      </div>
    </div>
  );
}
